import { Dialog, DialogTitle } from 'components/Elements';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { X, Command, Keyboard } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { defaultHotKeys } from 'constants/defaultHotKeys';
import { useTheme } from 'hooks/useTheme';

const COOKIE_KEY = 'custom_shortcuts';

export const Shortcuts = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [modifierKey, setModifierKey] = useState('Ctrl');
  const [groupedShortcuts, setGroupedShortcuts] = useState(defaultHotKeys);
  const [editingKey, setEditingKey] = useState(null);
  const [search, setSearch] = useState('');
  const [pressedKeys, setPressedKeys] = useState([]);
  const hasLoadedRef = useRef(false);
  const navigate = useNavigate();

  const { theme, background } = useTheme();

  const flatShortcuts = Object.entries(groupedShortcuts).reduce((acc, [group, keys]) => {
    Object.entries(keys).forEach(([k, v]) => {
      acc[k] = { ...v, group };
    });
    return acc;
  }, {});

  const handleOpen = () => setShowDialog(true);
  const handleClose = () => {
    setEditingKey(null);
    setPressedKeys([]);
    setShowDialog(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    handleClose();
  };

  const handleRebind = (originalKey, newKey) => {
    if (!newKey || flatShortcuts[newKey]) return;
    const newGrouped = { ...groupedShortcuts };
    const { group, label, path } = flatShortcuts[originalKey];
    newGrouped[group][newKey] = { label, path };
    delete newGrouped[group][originalKey];
    setGroupedShortcuts(newGrouped);
    Cookies.set(COOKIE_KEY, JSON.stringify(newGrouped), { expires: 365 });
    setEditingKey(null);
    setPressedKeys([]);
  };

  const resetToDefault = () => {
    setGroupedShortcuts(defaultHotKeys);
    Cookies.remove(COOKIE_KEY);
    setEditingKey(null);
    setPressedKeys([]);
    window.location.reload();
  };

  const handleKeyDown = useCallback(
    (e) => {
      if (editingKey && showDialog) {
        e.preventDefault();
        if (!pressedKeys.includes(e.key)) {
          const combo = [...pressedKeys, e.key];
          if (combo.length === 2) {
            const newKey = combo.sort().join('+');
            handleRebind(editingKey, newKey);
          } else {
            setPressedKeys(combo);
          }
        }
        return;
      }

      const key = e.key;
      if ((e.metaKey || e.ctrlKey) && key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowDialog(true);
      } else if ((e.metaKey || e.ctrlKey) && flatShortcuts[key]) {
        e.preventDefault();
        navigate(flatShortcuts[key].path);
      }
    },
    [editingKey, navigate, flatShortcuts, showDialog, pressedKeys]
  );

  useEffect(() => {
    const isMac = navigator.platform.toUpperCase().includes('MAC');
    setModifierKey(isMac ? 'Cmd' : 'Ctrl');

    if (!hasLoadedRef.current) {
      const saved = Cookies.get(COOKIE_KEY);
      if (saved) {
        try {
          setGroupedShortcuts(JSON.parse(saved));
        } catch (e) {
          console.error('Invalid shortcut cookie data', e);
        }
        hasLoadedRef.current = true;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div>
      <button
        onClick={handleOpen}
        className="w-full flex flex-row items-center space-x-1 text-center px-2 py-2 text-xs text-white hover:bg-white/20 rounded-md transition"
      >
        <Keyboard size={12} />
        <span>Shortcuts</span>
      </button>

      <Dialog isOpen={showDialog} onClose={handleClose}>
        <div
          className={`relative px-6 pt-5 pb-6 overflow-hidden transform transition-all sm:my-8 rounded-2xl text-left shadow-xl sm:align-middle sm:max-w-7xl w-full backdrop-blur-xl ${theme}`}
        >
          {/* Background layer */}
          {background && (
            <div className="absolute inset-0 z-0 pointer-events-none opacity-10">
              <img
                src={background}
                alt="Theme background"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Dialog Content */}
          <div className="relative z-10">
            <div className="flex flex-row items-center justify-between">
              <DialogTitle
                as="h3"
                className="text-lg font-semibold leading-6 text-white mb-1"
              >
                Wire24 Keyboard Shortcuts
              </DialogTitle>
              <button
                onClick={handleClose}
                className="text-white hover:text-white/70"
              >
                <X />
              </button>
            </div>

            <input
              type="text"
              placeholder="Search shortcuts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full mt-4 mb-4 px-4 py-2 rounded-md bg-white/10 text-white placeholder-white/50 focus:outline-none"
            />

            {editingKey && (
              <div className="mb-3 text-sm text-yellow-400">
                Press two keys to reassign shortcut for:{' '}
                <strong>{flatShortcuts[editingKey].label}</strong>
              </div>
            )}

            <DragDropContext onDragEnd={() => {}}>
              {Object.entries(groupedShortcuts).map(([group, keys]) => {
                const entries = Object.entries(keys).filter(([, value]) =>
                  value.label.toLowerCase().includes(search.toLowerCase())
                );
                if (entries.length === 0) return null;

                return (
                  <div key={group} className="mb-6">
                    <h4 className="text-white text-lg font-semibold mb-3">{group}</h4>
                    <Droppable droppableId={group}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className="grid grid-cols-1 gap-2"
                        >
                          {entries.map(([key, { label, path }], index) => (
                            <Draggable key={key} draggableId={key} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className="flex items-center justify-between text-sm text-white/80"
                                >
                                  <button
                                    type="button"
                                    onClick={() => handleNavigate(path)}
                                    className="hover:text-white transition text-sm"
                                  >
                                    {search ? (
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: label.replace(
                                            new RegExp(`(${search})`, 'gi'),
                                            (match) =>
                                              `<mark class='bg-yellow-300 text-black'>${match}</mark>`
                                          ),
                                        }}
                                      />
                                    ) : (
                                      label
                                    )}
                                  </button>
                                  <kbd
                                    className={`font-mono px-2 flex flex-row text-xs items-center space-x-2 py-1 rounded cursor-pointer transition ${
                                      editingKey === key
                                        ? 'bg-yellow-500 text-black'
                                        : 'bg-white/20'
                                    }`}
                                    onClick={() => {
                                      setPressedKeys([]);
                                      setEditingKey(key);
                                    }}
                                  >
                                    <Command size={12} />
                                    <span>{modifierKey} + {key}</span>
                                  </kbd>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </DragDropContext>

            <div className="text-right mt-6">
              <button
                onClick={resetToDefault}
                className="text-sm text-white/70 hover:text-white underline"
              >
                Reset to Default
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
